import store from '@/store'

const normalizeAction = (action) => {
  return String(action || 'get').toLowerCase().trim()
}

const parseScopeAction = (value) => {
  const raw = String(value || '').toLowerCase().trim()
  if (!raw || raw.includes('/')) {
    return null
  }

  const lastDot = raw.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === raw.length - 1) {
    return null
  }

  return {
    scope: raw.slice(0, lastDot),
    action: raw.slice(lastDot + 1),
  }
}

const normalizePath = (path) => {
  const raw = String(path || '').toLowerCase().trim()
  if (!raw) return ''

  const onlyPath = raw.split('?')[0].split('#')[0]
  const normalized = onlyPath.replace(/^\/+/g, '').replace(/\/+$/g, '')
  return normalized ? `/${normalized}` : ''
}

const routeTemplateToRegex = (routeTemplate) => {
  const normalizedTemplate = normalizePath(routeTemplate)
  const escaped = normalizedTemplate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const withParams = escaped.replace(/:([a-zA-Z0-9_]+)/g, '[^/]+')
  return new RegExp(`^${withParams}$`, 'i')
}

const matchesRoute = (path, routeTemplate) => {
  if (!routeTemplate) {
    return false
  }

  return routeTemplateToRegex(routeTemplate).test(path)
}

const matchesRouteOrChild = (path, routeTemplate) => {
  const normalizedPath = normalizePath(path)
  const normalizedTemplate = normalizePath(routeTemplate)
  if (!normalizedPath || !normalizedTemplate) {
    return false
  }

  if (matchesRoute(normalizedPath, normalizedTemplate)) {
    return true
  }

  const escaped = normalizedTemplate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const withParams = escaped.replace(/:([a-zA-Z0-9_]+)/g, '[^/]+')
  const childRegex = new RegExp(`^${withParams}(?:/.*)?$`, 'i')
  return childRegex.test(normalizedPath)
}

const hasRoutePermission = (path, action) => {
  const routePermissions = store.state.user?.permissions?.byScreenRoute || {}
  const normalizedPath = normalizePath(path)
  if (!normalizedPath) {
    return false
  }

  return Object.entries(routePermissions).some(([routeKey, permission]) => {
    const normalizedRoute = normalizePath(routeKey)
    if (!normalizedRoute) {
      return false
    }

    return matchesRouteOrChild(normalizedPath, normalizedRoute) && !!permission?.[action]
  })
}

const hasScopePermission = (scope, action) => {
  const normalizedScope = String(scope || '').toLowerCase().trim()
  if (!normalizedScope) {
    return false
  }
  const resourcePermissions = store.state.user?.permissions?.byResource || {}
  if (resourcePermissions[normalizedScope]?.[action] !== undefined) {
    return !!resourcePermissions[normalizedScope]?.[action]
  }

  const scopePermissions = store.state.user?.permissions?.byScope || {}
  return !!scopePermissions[normalizedScope]?.[action]
}

const hasPermission = (bindingValue) => {
  if (!bindingValue) {
    return false
  }

  if (typeof bindingValue === 'string') {
    const parsed = parseScopeAction(bindingValue)
    if (parsed) {
      return hasScopePermission(parsed.scope, normalizeAction(parsed.action))
    }

    return hasScopePermission(bindingValue, 'get')
  }

  const scopeOrPath = bindingValue.scope || bindingValue.path
  const action = normalizeAction(bindingValue.action || 'get')

  if (!scopeOrPath) {
    return false
  }

  if (String(scopeOrPath).includes('/')) {
    return hasRoutePermission(scopeOrPath, action)
  }

  return hasScopePermission(scopeOrPath, action)
}

const updateVisibility = (el, bindingValue) => {
  const allowed = hasPermission(bindingValue)
  el.style.display = allowed ? '' : 'none'
}

export default {
  mounted(el, binding) {
    updateVisibility(el, binding.value)
  },
  updated(el, binding) {
    updateVisibility(el, binding.value)
  },
}
